import React from 'react';
import { Divider, Paper, Stack, Typography } from '@mui/material';

/**
 * Cáscara común de las páginas legales (Términos, Privacidad, Eliminación de datos).
 *
 * Las tres repetían su propio encabezado centrado y su propio ancho, con jerarquías
 * distintas entre sí. Aquí viven una sola vez: ancho de lectura cómodo, títulos de
 * sección consistentes y el mismo aire en móvil que en escritorio.
 */
interface LegalPageProps {
    title: string;
    intro?: React.ReactNode;
    /** Fecha de última actualización que se muestra al pie del documento. */
    updated?: string;
    children: React.ReactNode;
}

export const LegalPage: React.FC<LegalPageProps> = ({ title, intro, updated, children }) => (
    <Stack spacing={3} sx={{ maxWidth: 720, mx: 'auto' }}>
        <Stack spacing={1} sx={{ pt: { xs: 1, sm: 2 } }}>
            <Typography variant="overline" color="text.secondary">
                KABANDÚ
            </Typography>
            <Typography variant="h4">{title}</Typography>
            {intro && (
                <Typography color="text.secondary" sx={{ maxWidth: 620 }}>
                    {intro}
                </Typography>
            )}
        </Stack>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
            <Stack spacing={3.5} divider={<Divider />}>
                {children}
            </Stack>
        </Paper>

        {updated && (
            <Typography variant="caption" color="text.secondary">
                Última actualización: {updated}
            </Typography>
        )}
    </Stack>
);

/** Una sección del documento: título y contenido con interlínea de lectura. */
export const LegalSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Stack spacing={1.25} component="section">
        <Typography variant="h6">{title}</Typography>
        {children}
    </Stack>
);

/** Párrafo del documento. */
export const LegalText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
        {children}
    </Typography>
);

/**
 * Punto numerado o con viñeta. El título va destacado y la explicación debajo: en las
 * listas anteriores (`ListItemText` con primary/secondary) el texto quedaba con el
 * tamaño y color por defecto del componente y se leía como una tabla, no como un documento.
 */
export const LegalItem: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
    <Stack spacing={0.25} sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
        {title && <Typography variant="subtitle2">{title}</Typography>}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {children}
        </Typography>
    </Stack>
);

export default LegalPage;
